import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import HeroSection from './components/HeroSection';
import ServiceShowcase from './components/ServiceShowcase';
import InteractiveServiceFinder from './components/InteractiveServiceFinder';
import TrustIndicators from './components/TrustIndicators';
import PlatformBenefits from './components/PlatformBenefits';
import FeaturedContractors from './components/FeaturedContractors';
import WatWeUitdragen from './components/WatWeUitdragen';
import EUNetwerk from './components/EUNetwerk';
import VoorUBlok from './components/VoorUBlok';
import BVMVoordeel from './components/BVMVoordeel';
import VolledigCompliant from './components/VolledigCompliant';
import PlaatsingsProces from './components/PlaatsingsProces';
import CTAContact from './components/CTAContact';
import TickerTape from './components/TickerTape';

const Homepage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 z-[1000]" style={{ background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1A1A1A' }}>
        <div className="h-full px-6 lg:px-10 flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/assets/images/Untitled-1774116510083.jpeg" alt="BVM Group Logo" className="h-10 w-auto object-contain" />
            <span className="font-heading font-bold text-lg text-white">BVM Group</span>
          </Link>

          {/* Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {[
              { label: 'Opdrachten', href: '#services' },
              { label: 'Voordelen', href: '#benefits' },
              { label: 'Vakmensen', href: '#contractors' },
              { label: 'Over Ons', href: '#about' },
            ]?.map((item) => (
              <a
                key={item?.label}
                href={item?.href}
                className="text-sm font-body font-medium transition-colors duration-200 hover:text-white relative group"
                style={{ color: '#888888' }}
              >
                {item?.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300" style={{ background: '#00E5A0' }} />
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <Link to="/login">
              <motion.button
                className="px-4 py-2 text-sm font-body font-medium rounded-sm border transition-all duration-200 text-white"
                style={{ borderColor: '#2A2A2A', background: 'transparent' }}
                whileHover={{ borderColor: '#00E5A0', color: '#00E5A0' }}
                whileTap={{ scale: 0.97 }}
              >
                Inloggen
              </motion.button>
            </Link>
            <Link to="/login">
              <motion.button
                className="px-5 py-2 text-sm font-body font-semibold rounded-sm transition-all duration-200"
                style={{ background: '#00E5A0', color: '#0A0A0A' }}
                whileHover={{ scale: 1.03, background: '#00ffb3' }}
                whileTap={{ scale: 0.97 }}
              >
                Aan de slag
              </motion.button>
            </Link>
            {/* Mobile menu toggle */}
            <button
              className="md:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={20} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div
            className="md:hidden absolute top-16 left-0 right-0 py-4 px-6"
            style={{ background: '#0A0A0A', borderBottom: '1px solid #1A1A1A' }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {[
              { label: 'Opdrachten', href: '#services' },
              { label: 'Voordelen', href: '#benefits' },
              { label: 'Vakmensen', href: '#contractors' },
              { label: 'Over Ons', href: '#about' },
            ]?.map((item) => (
              <a
                key={item?.label}
                href={item?.href}
                className="block py-3 text-sm font-body border-b"
                style={{ color: '#888888', borderColor: '#1A1A1A' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item?.label}
              </a>
            ))}
            <div className="flex gap-3 mt-4">
              <Link to="/login" className="flex-1">
                <button className="w-full py-3 text-sm font-body font-medium rounded-sm border text-white" style={{ borderColor: '#2A2A2A' }}>
                  Inloggen
                </button>
              </Link>
              <Link to="/login" className="flex-1">
                <button className="w-full py-3 text-sm font-body font-semibold rounded-sm" style={{ background: '#00E5A0', color: '#0A0A0A' }}>
                  Aan de slag
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </header>
      <main className="pt-16">
        <HeroSection />
        <TickerTape />

        {/* Featured New Services Callout */}
        <motion.section
          className="py-8 relative overflow-hidden"
          style={{ background: '#0D0D0D', borderTop: '1px solid #1A1A1A', borderBottom: '1px solid #1A1A1A' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <motion.div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: '#00E5A0' }}
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Icon name="Sparkles" size={24} color="#0A0A0A" />
                </motion.div>
                <div>
                  <h3 className="text-xl font-heading font-bold" style={{ color: '#00E5A0' }}>
                    Nieuw! Uitgebreide vakgebieden
                  </h3>
                  <p className="text-sm font-body" style={{ color: '#666666' }}>
                    Grondwerken, vrachtwagen chauffeurs, loodgieters, kraan operators en stratenmakers nu beschikbaar
                  </p>
                </div>
              </div>
              <motion.a
                href="#services"
                className="flex items-center gap-2 px-6 py-3 text-sm font-body font-semibold rounded-sm transition-all duration-200"
                style={{ background: '#00E5A0', color: '#0A0A0A' }}
                whileHover={{ scale: 1.05, background: '#00ffb3' }}
                whileTap={{ scale: 0.95 }}
              >
                Bekijk alle vakgebieden
                <Icon name="ArrowRight" size={18} color="#0A0A0A" />
              </motion.a>
            </div>
          </div>
        </motion.section>

        <WatWeUitdragen />
        <EUNetwerk />
        <VoorUBlok />
        <ServiceShowcase />
        <BVMVoordeel />
        <InteractiveServiceFinder />
        <TrustIndicators />
        <PlatformBenefits />
        <VolledigCompliant />
        <PlaatsingsProces />
        <FeaturedContractors />

        {/* About section */}
        <section id="about" className="py-16 md:py-20 bg-[#0A0A0A] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 rounded-full blur-[120px] opacity-5" style={{ background: '#00E5A0' }} />
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <motion.h2
                className="text-3xl md:text-4xl font-heading font-bold mb-4"
                style={{ color: '#00E5A0' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Over BVM Group
              </motion.h2>
              <motion.p
                className="text-lg max-w-3xl mx-auto"
                style={{ color: '#888888' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Wij verbinden bedrijven met gekwalificeerde vakmensen voor duurzame technische installaties
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px mb-12" style={{ background: '#1A1A1A' }}>
              {[
                { icon: 'Target', title: 'Onze Missie', text: 'Het toegankelijk maken van duurzame technische installaties door opdrachtgevers direct te verbinden met gekwalificeerde professionals.' },
                { icon: 'Eye', title: 'Onze Visie', text: 'Een toonaangevend platform worden voor technische installaties in de Benelux, met focus op duurzaamheid en kwaliteit.' },
                { icon: 'Award', title: 'Onze Waarden', text: 'Transparantie, betrouwbaarheid en vakmanschap staan centraal in alles wat we doen. Kwaliteit boven kwantiteit.' }
              ]?.map((item, i) => (
                <motion.div
                  key={i}
                  className="p-8 group"
                  style={{ background: '#0A0A0A' }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  whileHover={{ background: '#0F0F0F', y: -5 }}
                >
                  <div className="w-12 h-12 rounded flex items-center justify-center mb-4" style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid rgba(0,229,160,0.2)' }}>
                    <Icon name={item?.icon} size={24} color="#00E5A0" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-white mb-3">{item?.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#888888' }}>{item?.text}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="p-8"
              style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-heading font-semibold text-white mb-4">Waarom BVM Group?</h3>
                  <ul className="space-y-3">
                    {[
                      'Gecertificeerde vakmensen met bewezen expertise',
                      'Transparante prijzen en duidelijke communicatie',
                      'Snelle matching tussen opdracht en vakman',
                      'Focus op duurzame en toekomstbestendige oplossingen'
                    ]?.map((feature, index) => (
                      <motion.li
                        key={index}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                      >
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(0,229,160,0.1)' }}>
                          <Icon name="Check" size={14} color="#00E5A0" />
                        </div>
                        <span className="text-sm font-body" style={{ color: '#888888' }}>{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: '2500+', label: 'Vakmensen', color: '#00E5A0' },
                    { value: '5000+', label: 'Opdrachten', color: '#00E5A0' },
                    { value: '98%', label: 'Tevredenheid', color: '#00E5A0' },
                    { value: '24/7', label: 'Ondersteuning', color: '#00E5A0' }
                  ]?.map((stat, i) => (
                    <motion.div
                      key={i}
                      className="p-6 text-center"
                      style={{ background: '#141414', border: '1px solid #2A2A2A' }}
                      whileHover={{ scale: 1.05, borderColor: '#00E5A0' }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="text-3xl font-heading font-bold mb-2" style={{ color: stat?.color }}>{stat?.value}</div>
                      <div className="text-sm font-body" style={{ color: '#666666' }}>{stat?.label}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <CTAContact />
      </main>
      {/* Footer */}
      <footer className="py-12 border-t" style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/assets/images/Untitled-1774116510083.jpeg" alt="BVM Group Logo" className="h-10 w-auto object-contain" />
              </div>
              <span className="font-heading font-bold text-lg text-white">BVM Group</span>
              <p className="text-sm font-body leading-relaxed" style={{ color: '#555555' }}>
                Het platform voor technische installaties, vakmensen en affiliates
              </p>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Opdrachten</h4>
              <ul className="space-y-2">
                {['Zonnepanelen', 'Warmtepompen', 'Thuisbatterijen', 'Elektrische installaties', 'HVAC systemen']?.map(l => (
                  <li key={l}><a href="#services" className="text-sm font-body transition-colors hover:text-white" style={{ color: '#666666' }}>{l}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><Link to="/opdracht-plaatsen" className="text-sm font-body transition-colors hover:text-white" style={{ color: '#666666' }}>Voor Opdrachtgevers</Link></li>
                <li><Link to="/opdracht-zoeken" className="text-sm font-body transition-colors hover:text-white" style={{ color: '#666666' }}>Voor Opdrachtnemers</Link></li>
                <li><Link to="/wordt-partner" className="text-sm font-body transition-colors hover:text-white" style={{ color: '#666666' }}>Voor Partners</Link></li>
                <li><a href="#about" className="text-sm font-body transition-colors hover:text-white" style={{ color: '#666666' }}>Over Ons</a></li>
                <li><a href="#benefits" className="text-sm font-body transition-colors hover:text-white" style={{ color: '#666666' }}>Hoe het werkt</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Ondersteuning</h4>
              <ul className="space-y-2">
                {['Privacy Policy', 'Algemene Voorwaarden', 'Contact']?.map(l => (
                  <li key={l}><Link to="/login" className="text-sm font-body transition-colors hover:text-white" style={{ color: '#666666' }}>{l}</Link></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t text-center" style={{ borderColor: '#1A1A1A' }}>
            <p className="text-xs font-body" style={{ color: '#444444' }}>
              © {new Date()?.getFullYear()} BVM Group. Alle rechten voorbehouden.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;